import {FC, useEffect, useState, useMemo, useRef} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {useLocation, useHistory} from 'react-router-dom'
import moment from 'moment'
import Paginator from '../../../components/Paginator'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import {PaymentTransactionModel} from '../models/PaymentTransactionModel'
import {PaymentRequestFilterModel} from '../models/PaymentRequestFilterModel'
import {
  paymentRequestFilterDataChange,
  getAllPaymentRequest,
  selectPaymentRequestList,
  selectPaymentRequestFilter,
  selectBillingLoading,
  downloadPaymentDocument,
} from '../redux/BillingRedux'
import {selectUser} from '../../../modules/auth/redux/AuthRedux'
import {toast} from 'react-toastify'
import {DATE_FORMAT, PAYMENT_METHOD} from '../../../shared/constants'
import {PaymentRequestStatus} from '../constants'
import {displayAmountBaseOnCurrency} from '../../pricing/utils'
import {downloadDocument} from '../redux/BillingApi'

type MonthOption = {
  value: string
  label: string
  startDate: string
  endDate: string
}

const BillingTransactionsTab: FC = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const didRequest = useRef(false)
  const data: PaymentTransactionModel[] = useAppSelector(selectPaymentRequestList)
  const filter: PaymentRequestFilterModel = useAppSelector(selectPaymentRequestFilter)
  const loading = useAppSelector(selectBillingLoading)
  const user = useAppSelector(selectUser)
  const [selectedMonth, setSelectedMonth] = useState<string>('thisMonth')
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [isDownloading, setIsDownloading] = useState<boolean>(false)

  // Generate month options
  const monthOptions = useMemo((): MonthOption[] => {
    const currentMonth = moment()
    const lastMonth = moment().subtract(1, 'month')

    return [
      {
        value: 'thisMonth',
        label: intl.formatMessage({id: 'BILLING.FILTER.THIS_MONTH'}),
        startDate: currentMonth.clone().startOf('month').format(DATE_FORMAT),
        endDate: currentMonth.clone().endOf('month').format(DATE_FORMAT),
      },
      {
        value: 'lastMonth',
        label: intl.formatMessage({id: 'BILLING.FILTER.LAST_MONTH'}),
        startDate: lastMonth.clone().startOf('month').format(DATE_FORMAT),
        endDate: lastMonth.clone().endOf('month').format(DATE_FORMAT),
      },
    ]
  }, [intl])

  useEffect(() => {
    if (!didRequest.current) {
      const searchParams = new URLSearchParams(location.search)
      const monthParam = searchParams.get('month')

      if (monthParam && monthParam.length === 6) {
        const mm = monthParam.substring(0, 2)
        const yyyy = monthParam.substring(2, 6)
        const parsedMonth = moment(`${yyyy}-${mm}-01`, 'YYYY-MM-DD')

        if (parsedMonth.isValid()) {
          const startDate = parsedMonth.clone().startOf('month').format(DATE_FORMAT)
          const endDate = parsedMonth.clone().endOf('month').format(DATE_FORMAT)

          setSelectedDate(parsedMonth.toDate())

          const matchingOption = monthOptions.find((option) => {
            const optionStart = moment(option.startDate)
            return (
              optionStart.isSame(parsedMonth, 'month') &&
              optionStart.isSame(parsedMonth, 'year')
            )
          })
          setSelectedMonth(matchingOption ? matchingOption.value : '')

          const newFilter: PaymentRequestFilterModel = {
            ...filter,
            startDate,
            endDate,
            page: 1,
          }
          dispatch(paymentRequestFilterDataChange(newFilter))

          history.replace('/billings')
        }
      }

      dispatch(getAllPaymentRequest())
      didRequest.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleMonthChange = (monthValue: string) => {
    setSelectedMonth(monthValue)

    const selectedOption = monthOptions.find((option) => option.value === monthValue)
    if (selectedOption) {
      // Update date picker to show the selected month
      const selectedMoment = moment(selectedOption.startDate)
      setSelectedDate(selectedMoment.toDate())

      const newFilter = {
        ...filter,
        startDate: selectedOption.startDate,
        endDate: selectedOption.endDate,
        page: 1,
      }
      dispatch(paymentRequestFilterDataChange(newFilter))
      dispatch(getAllPaymentRequest())
    }
  }

  const handleMonthPickerChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date)
      const selectedMoment = moment(date)
      const startDate = selectedMoment.clone().startOf('month').format(DATE_FORMAT)
      const endDate = selectedMoment.clone().endOf('month').format(DATE_FORMAT)

      // Check if the selected date matches any quick month option
      const matchingOption = monthOptions.find((option) => {
        const optionStartDate = moment(option.startDate)
        return (
          optionStartDate.isSame(selectedMoment, 'month') &&
          optionStartDate.isSame(selectedMoment, 'year')
        )
      })

      if (matchingOption) {
        setSelectedMonth(matchingOption.value)
      } else {
        setSelectedMonth('') // Clear quick month selection if no match
      }

      const newFilter = {
        ...filter,
        startDate,
        endDate,
        page: 1,
      }
      dispatch(paymentRequestFilterDataChange(newFilter))
      dispatch(getAllPaymentRequest())
    }
  }

  const handlePageChanged = (targetPage: number) => {
    const newFilter = {...filter, page: targetPage}
    dispatch(paymentRequestFilterDataChange(newFilter))
    dispatch(getAllPaymentRequest())
  }

  const handleDownloadPaymentRequest = () => {
    if (!user?.id) {
      toast.error('User not found')
      return
    }

    // Get the current month in YYYYMM format
    let month = ''
    if (selectedDate) {
      month = moment(selectedDate).format('YYYYMM')
    } else if (selectedMonth) {
      const selectedOption = monthOptions.find((option) => option.value === selectedMonth)
      if (selectedOption) {
        month = moment(selectedOption.startDate).format('YYYYMM')
      }
    }

    if (!month) {
      toast.error('Month not selected')
      return
    }

    // Set loading state
    setIsDownloading(true)

    // Dispatch download action
    dispatch(downloadPaymentDocument({userId: user.id.toString(), month}))

    // Reset loading state after 4 seconds
    setTimeout(() => {
      setIsDownloading(false)
    }, 2500)
  }

  // Check if should show download button
  const shouldShowDownloadButton = () => {
    // Check date picker selection first
    if (selectedDate) {
      const selectedMoment = moment(selectedDate)
      const currentMonth = moment().startOf('month')
      const isInPast = selectedMoment.isBefore(currentMonth)
      const hasBankTransfer = data.some((transaction) => transaction.method === PAYMENT_METHOD.BANK)
      return isInPast && hasBankTransfer
    }

    // Fallback to button selection (only if selectedMonth is not empty)
    if (!selectedMonth) return false

    const selectedOption = monthOptions.find((option) => option.value === selectedMonth)
    if (!selectedOption) return false

    const selectedDateFromOption = moment(selectedOption.startDate)
    const currentMonth = moment().startOf('month')

    // Check if selected month is in the past
    const isInPast = selectedDateFromOption.isBefore(currentMonth)

    // Check if there's at least one bank transfer transaction
    const hasBankTransfer = data.some((transaction) => transaction.method === PAYMENT_METHOD.BANK)

    return isInPast && hasBankTransfer
  }

  const getStatusBadgeClass = (status: PaymentRequestStatus) => {
    switch (status) {
      case PaymentRequestStatus.COMPLETED:
        return 'badge-light-success'
      case PaymentRequestStatus.PENDING:
        return 'badge-light-warning'
      case PaymentRequestStatus.FAIL:
        return 'badge-light-danger'
      case PaymentRequestStatus.CANCEL:
        return 'badge-light-secondary'
      default:
        return 'badge-light-primary'
    }
  }

  const getPaymentMethodLabel = (method: PAYMENT_METHOD) => {
    return intl.formatMessage({id: `BILLING.PAYMENT_METHOD.${method}`})
  }

  const getPaymentMethodIcon = (method: PAYMENT_METHOD) => {
    switch (method) {
      case PAYMENT_METHOD.BANK:
        return <i className='fas fa-university fs-5 me-2'></i>
      case PAYMENT_METHOD.VISA:
        return <i className='fas fa-credit-card fs-5 me-2'></i>
      case PAYMENT_METHOD.STRIPE:
        return <i className='fas fa-credit-card fs-5 me-2'></i>
      default:
        return null
    }
  }

  const handleDownloadTransactionDocument = (transactionId: number) => {
    if (!user?.id) {
      toast.error('User not found')
      return
    }

    // Get the current month in YYYYMM format
    let month = ''
    if (selectedDate) {
      month = moment(selectedDate).format('YYYYMM')
    } else if (selectedMonth) {
      const selectedOption = monthOptions.find((option) => option.value === selectedMonth)
      if (selectedOption) {
        month = moment(selectedOption.startDate).format('YYYYMM')
      }
    }

    if (!month) {
      toast.error('Month not selected')
      return
    }
    // Set loading state
    setIsDownloading(true)

    // Dispatch download action
    dispatch(
      downloadPaymentDocument({
        userId: user.id.toString(),
        month,
        paymentTransactionId: transactionId.toString(),
      })
    )

    // Reset loading state after 4 seconds
    setTimeout(() => {
      setIsDownloading(false)
    }, 2500)
  }

  // Check if any transaction has STRIPE payment method
  const hasStripeTransactions = data.some(
    (transaction) => transaction.method === PAYMENT_METHOD.STRIPE
  )

  const renderTransaction = (transaction: PaymentTransactionModel) => {
    return (
      <tr key={transaction.id}>
        <td>{moment(transaction.createdAt).format(process.env.REACT_APP_DATE_FORMAT)}</td>
        <td className='text-gray-600 fw-semibold'>
          {intl.formatMessage({id: `BILLING.PAY_TYPE.${transaction.payType}`})}
        </td>
        <td className='text-gray-600 fw-semibold'>
          {intl.formatMessage({id: `${transaction.targetName}`}) || '-'}
        </td>
        <td>
          <span className='badge badge-light fs-7 fw-semibold d-flex align-items-center'>
            {getPaymentMethodIcon(transaction.method)}
            {getPaymentMethodLabel(transaction.method)}
          </span>
        </td>
        <td className='text-gray-800 fw-bolder'>
          {displayAmountBaseOnCurrency(transaction.total - transaction.tax, transaction.currency)}
        </td>
        <td className='text-primary fw-bolder'>
          {displayAmountBaseOnCurrency(transaction.total, transaction.currency)}
        </td>
        <td>
          <span className={`badge py-3 px-4 fs-7 ${getStatusBadgeClass(transaction.status)}`}>
            {intl.formatMessage({id: `BILLING.STATUS.${transaction.status}`})}
          </span>
        </td>
        {hasStripeTransactions && (
          <td className='text-end'>
            {transaction.method === PAYMENT_METHOD.STRIPE ? (
              <button
                className='btn btn-light-primary btn-sm'
                onClick={() => handleDownloadTransactionDocument(transaction.id)}
                title={intl.formatMessage({id: 'BILLING.ACTION.DOWNLOAD_DOCUMENT'})}
              >
                <i className='fas fa-download fs-6'></i>
              </button>
            ) : (
              <span>-</span>
            )}
          </td>
        )}
      </tr>
    )
  }

  return (
    <>
      {/* Filter Section */}
      <div className='card-header border-0 pt-5 pb-3'>
        <div className='row w-100 g-4'>
          <div className='col-md-8'>
            <label className='form-label fs-6 fw-bolder'>
              {intl.formatMessage({id: 'BILLING.FILTER.MONTH'})}
            </label>
            <div className='d-flex gap-3'>
              <DatePicker
                selected={selectedDate}
                onChange={handleMonthPickerChange}
                dateFormat='MM/yyyy'
                showMonthYearPicker
                className='form-control form-control-sm'
                placeholderText={intl.formatMessage({id: 'BILLING.FILTER.SELECT_MONTH'})}
              />
              {monthOptions.map((option) => (
                <button
                  key={option.value}
                  type='button'
                  className={`btn btn-sm ${
                    selectedMonth === option.value ? 'btn-primary' : 'btn-light'
                  }`}
                  onClick={() => handleMonthChange(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className='col-md-4 d-flex align-items-end justify-content-end'>
            {shouldShowDownloadButton() && (
              <button
                className='btn btn-primary btn-sm d-flex align-items-center gap-2'
                onClick={handleDownloadPaymentRequest}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <span
                    className='spinner-border spinner-border-sm'
                    role='status'
                    aria-hidden='true'
                  ></span>
                ) : (
                  <i className='fas fa-download fs-6'></i>
                )}
                {intl.formatMessage({id: 'BILLING.ACTION.DOWNLOAD_PAYMENT_REQUEST'})}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className='table-responsive'>
        {/* begin::Table */}
        <table className='table table-hover table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
          {/* begin::Table head */}
          <thead>
            <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
              <th className='min-w-120px'>{intl.formatMessage({id: 'BILLING.TABLE.DATE'})}</th>
              <th className='min-w-200px'>{intl.formatMessage({id: 'BILLING.TABLE.PAY_FOR'})}</th>
              <th className='min-w-120px'>{intl.formatMessage({id: 'BILLING.TABLE.TARGET'})}</th>
              <th className='min-w-120px'>{intl.formatMessage({id: 'BILLING.TABLE.METHOD'})}</th>
              <th className='min-w-120px'>{intl.formatMessage({id: 'BILLING.TABLE.AMOUNT'})}</th>
              <th className='min-w-120px'>
                {intl.formatMessage({id: 'BILLING.TABLE.TOTAL_AMOUNT'})}
              </th>
              <th className='min-w-120px'>{intl.formatMessage({id: 'BILLING.TABLE.STATUS'})}</th>
              {hasStripeTransactions && (
                <th className='min-w-100px text-end'>
                  {intl.formatMessage({id: 'BILLING.TABLE.ACTION'})}
                </th>
              )}
            </tr>
          </thead>
          {/* end::Table head */}
          {/* begin::Table body */}
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={hasStripeTransactions ? 8 : 7} className='text-center'>
                  <div className='spinner-border' role='status'>
                    <span className='visually-hidden'>
                      {intl.formatMessage({id: 'BILLING.LOADING'})}
                    </span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={hasStripeTransactions ? 8 : 7} className='text-center text-muted'>
                  {intl.formatMessage({id: 'BILLING.EMPTY.MESSAGE'})}
                </td>
              </tr>
            ) : (
              data.map((transaction) => renderTransaction(transaction))
            )}
          </tbody>
          {/* end::Table body */}
        </table>
        {/* end::Table */}
      </div>
      <div className='d-flex justify-content-end pt-10'>
        <Paginator
          currentPage={filter.page}
          totalPages={filter.pageCount || 1}
          onPageChange={handlePageChanged}
        />
      </div>
    </>
  )
}

export default BillingTransactionsTab
